import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import AppBar from './AppBar';
import JournalEntry from './JournalEntry';

function Journal() {
  const [habits, setHabits] = useState([]);
  const [notes, setNotes] = useState({});
  const [showHabitsModal, setShowHabitsModal] = useState(false);
  
  // Generate dates from Jan 1 2025 to current date
  const dates = (() => {
    const result = [];
    const startDate = new Date(2025, 0, 1);
    const endDate = new Date();
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      result.push({
        date: new Date(d),
        dateStr: d.toISOString().split('T')[0],
        formattedDate: d.toLocaleDateString('en-US', { 
          weekday: 'short', day: 'numeric', month: 'short'
        }).replace(/(\w+), (\d+) (\w+)/, '$1, $2 $3')
      });
    }
    return result;
  })();

  // Fetch habits and notes for current user
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const habitsQuery = query(
      collection(db, "habits"),
      where("uid", "==", currentUser.uid)
    );
    
    const notesQuery = query(
      collection(db, "notes_v2"),
      where("uid", "==", currentUser.uid),
      where("type", "==", "journal")
    );

    const unsubscribeHabits = onSnapshot(habitsQuery, (querySnapshot) => {
      console.log(`[${new Date().toISOString()}] Journal listener triggered - ${querySnapshot.docs.length} docs`);
      const habitsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setHabits(habitsData.sort((a, b) => 
        new Date(a.createdAt) - new Date(b.createdAt)
      ));
    });

    const unsubscribeNotes = onSnapshot(notesQuery, (querySnapshot) => {
      console.log(`[${new Date().toISOString()}] Journal listener triggered - ${querySnapshot.docs.length} docs`);
      const notesData = {};
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        notesData[data.dateStr] = {
          id: doc.id,
          ...data
        };
      });
      setNotes(notesData);
    });

    return () => {
      unsubscribeHabits();
      unsubscribeNotes();
    };
  }, []);

  const handleNoteChange = async (dateStr, e) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const now = new Date().toISOString();
      const content = e.target.value;

      const noteData = {
        content,
        dateStr,
        updatedAt: now,
        uid: currentUser.uid,
        type: 'journal'
      };

      setNotes(prevNotes => ({
        ...prevNotes,
        [dateStr]: { 
          ...(prevNotes[dateStr] || {}), 
          ...noteData 
        }
      }));

      // Handle Firestore update/create
      if (notes[dateStr]?.id) {
        await updateDoc(doc(db, "notes_v2", notes[dateStr].id), {
          content,
          updatedAt: now
        });
      } else {
        await addDoc(collection(db, "notes_v2"), {
          ...noteData,
          createdAt: now,
          embedding: []
        });
      }
    } catch (error) {
      console.error("Error updating note:", error);
    }
  };

  const handleCreateHabit = async (habitName) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !habitName?.trim()) return;

      await addDoc(collection(db, "habits"), {
        name: habitName.trim(),
        createdAt: new Date().toISOString(),
        uid: currentUser.uid,
        entries: {}
      });
    } catch (error) {
      console.error("Error creating habit:", error);
    }
  };

  const handleDeleteHabit = async (habitId) => {
    try {
      await deleteDoc(doc(db, "habits", habitId));
    } catch (error) {
      console.error("Error deleting habit:", error);
    }
  };

  const toggleHabitEntry = async (habitId, dateStr) => {
    try {
      const habit = habits.find(h => h.id === habitId);
      if (!habit) return;
      
      await updateDoc(doc(db, "habits", habitId), {
        [`entries.${dateStr}`]: !habit.entries?.[dateStr]
      });
    } catch (error) {
      console.error("Error toggling habit entry:", error);
    }
  };

  const handleEditHabit = async (habitId, newName) => {
    try {
      if (!newName?.trim()) return;
      await updateDoc(doc(db, "habits", habitId), {
        name: newName.trim()
      });
    } catch (error) {
      console.error("Error updating habit:", error);
    }
  };

  return (
    <div className="flex-col h-[100dvh] overflow-hidden">
      <div className="flex bg-gray-50 h-[100dvh]">
        {/* Main habits panel */}
        <div className="flex-1 max-w-[440px] border-r border-gray-200 bg-white flex flex-col pb-[var(--app-bar-height,120px)]">
          {/* Header */}
          <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
            <h1 className="text-sm font-semibold text-gray-500">JOURNAL</h1>
            <button
              onClick={() => setShowHabitsModal(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Manage Habits
            </button>
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col-reverse">
            <div className="flex flex-col">
              {dates.map(({ date, dateStr, formattedDate }) => (
                <JournalEntry
                  key={dateStr}
                  dateStr={dateStr}
                  formattedDate={formattedDate}
                  note={notes[dateStr]}
                  habits={habits}
                  onNoteChange={handleNoteChange}
                  onToggleHabit={toggleHabitEntry}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <AppBar
        className="fixed bottom-0 left-0 right-0"
        // hideInput={true}
        onSubmit={() => {}}
      />

      {/* Habits modal */}
      {showHabitsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Manage Habits</h2>
              <button onClick={() => setShowHabitsModal(false)} className="text-gray-500">
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              {habits.map(habit => (
                <div key={habit.id} className="flex justify-between items-center">
                  <span>{habit.name}</span>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        const newName = prompt("Enter new habit name:", habit.name);
                        if (newName) handleEditHabit(habit.id, newName);
                      }}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteHabit(habit.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const name = prompt("Enter new habit name:");
                  if (name) handleCreateHabit(name);
                }}
                className="w-full py-2 px-4 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Add New Habit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Journal;
